import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Link,
  Copy,
  RefreshCw,
  Trash2,
  MessageCircle,
  Loader2,
  Clock
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/modules/shared/components/ui/dialog'
import { Button } from '@/modules/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/shared/components/ui/select'
import { Label } from '@/modules/shared/components/ui/label'
import {
  createCatalogoTokenService,
  getCatalogoTokenService,
  invalidateCatalogoTokenService,
  validateCatalogoTokenService,
  generateCatalogoLinkFromToken,
  generateJwtTokenService,
  type ClienteCatalogo
} from '../services/cliente-catalogo.service'
import {
  saveCatalogoToken,
  getCatalogoToken,
  removeCatalogoToken,
  type StoredTokenData
} from '../services/catalogo-token-manager.service'
import { useAuthStore } from '@/modules/shared/stores/auth.store'
import { getVendedorIdFromJWT } from '@/modules/shared/utils/general-helper'

type CatalogoLinkDialogProps = {
  isOpen: boolean
  onClose: () => void
  clienteId: string
  clienteName: string
  clienteCnpj: string
  clienteEmail?: string
  clienteFone?: string
  tabelaPrecoId?: string
  vigenciaId?: string
}

export function CatalogoLinkDialogComponent({
  isOpen,
  onClose,
  clienteId,
  clienteName,
  clienteCnpj,
  clienteEmail,
  clienteFone,
  tabelaPrecoId,
  vigenciaId,
}: CatalogoLinkDialogProps) {
  const { user, token } = useAuthStore()
  const [tokenData, setTokenData] = useState<StoredTokenData | null>(null)
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [expirationHours, setExpirationHours] = useState<string>('24')

  // Obtém o companyCode do localStorage
  const getCompanyCode = (): number => {
    try {
      const companyCode = localStorage.getItem('companyCode')
      return companyCode ? Number.parseInt(companyCode) : 1
    } catch (error) {
      console.error('Erro ao obter companyCode:', error)
      return 1
    }
  }

  const empresaId = getCompanyCode()

  // Extrai o vendedorId do JWT
  const vendedorId = token ? getVendedorIdFromJWT(token) : null

  // Verifica se o cliente possui dados obrigatórios para gerar o catálogo
  const hasRequiredData = !!(tabelaPrecoId && vigenciaId && tabelaPrecoId.trim() !== '' && vigenciaId.trim() !== '')

  // Função para verificar se o token é válido (mais simples que isTokenValid)
  const isServerTokenValid = (tokenData: StoredTokenData | null): boolean => {
    if (!tokenData || !tokenData.ativo) {
      console.log('Token inválido: não existe ou inativo', { tokenData })
      return false
    }

    // Se o token veio do servidor através do validate, confiamos na resposta da API
    // Só verificamos se a data de expiração não passou
    const now = new Date()
    const expiracao = new Date(tokenData.dataExpiracao)
    const isValid = expiracao > now

    console.log('Verificação de expiração:', {
      now: now.toISOString(),
      expiracao: expiracao.toISOString(),
      isValid
    })

    return isValid
  }

  const loadCatalogoToken = async () => {
    if (!clienteCnpj || !vendedorId || !token || !hasRequiredData) return

    setLoading(true)
    try {
      // Usa o novo endpoint de validação para buscar tokens existentes
      const response = await validateCatalogoTokenService({
        empresaId,
        clienteId: Number.parseInt(clienteId),
        vendedorId,
        jwtPart: token, // Envia o JWT completo do vendedor
      })

      if (response.valido && response.dados) {
        // Token válido encontrado no servidor
        const tokenData = {
          clienteCnpj,
          empresaId,
          token: response.dados.token || '',
          dataCriacao: response.dados.dataCriacao || new Date().toISOString(),
          dataExpiracao: response.dados.dataExpiracao || new Date().toISOString(),
          ativo: true, // Se a API disse que é válido, então está ativo
        }

        // Salva no localStorage
        saveCatalogoToken(clienteCnpj, response.dados)
        setTokenData(tokenData)
        console.log('Token válido carregado do servidor:', tokenData)
      } else {
        // Nenhum token válido encontrado - estado normal
        setTokenData(null)
        console.log('Estado normal: cliente não possui token de catálogo ainda:', response.mensagem)
      }
    } catch (error: any) {
      // Se a API retornar erro, não mostra toast pois pode ser estado normal (sem token)
      console.log('Resposta da API de validação:', error?.response?.data?.mensagem || error?.message)
      setTokenData(null)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateToken = async () => {
    setActionLoading('generate')
    try {
      // Validação de campos obrigatórios
      if (!vendedorId || !token) {
        toast.error('Erro de autenticação. Faça login novamente.')
        return
      }

      if (!tabelaPrecoId || !vigenciaId || tabelaPrecoId.trim() === '' || vigenciaId.trim() === '') {
        const missingData = []
        if (!tabelaPrecoId || tabelaPrecoId.trim() === '') missingData.push('tabela de preço')
        if (!vigenciaId || vigenciaId.trim() === '') missingData.push('vigência')

        toast.error(`Não é possível gerar o link: cliente sem ${missingData.join(' e ')} configurada(s).`)
        console.error('Campos obrigatórios faltando:', {
          vendedorId,
          token: !!token,
          tabelaPrecoId,
          vigenciaId
        })
        return
      }

      // Calcula a data de expiração baseada nas horas selecionadas
      const horasExpiracao = Number.parseInt(expirationHours)
      const dataExpiracao = new Date()
      dataExpiracao.setHours(dataExpiracao.getHours() + horasExpiracao)

      console.log('Gerando token com expiração em', horasExpiracao, 'horas:', dataExpiracao.toISOString())

      // Gera o JWT token com o tempo de expiração
      const jwtToken = await generateJwtTokenService(dataExpiracao)
      console.log('JWT Token gerado:', jwtToken)

      const requestData = {
        empresaId,
        clienteCnpj,
        clienteId: Number.parseInt(clienteId),
        clienteNome: clienteName,
        clienteEmail: clienteEmail || '',
        clienteFone: clienteFone || '',
        vendedorId,
        vendedorJwt: jwtToken, // Usa o JWT gerado ao invés do token do usuário
        tabelaPrecoId: tabelaPrecoId,
        vigenciaId: vigenciaId,
      }

      console.log('EmpresaId (companyCode):', empresaId)
      console.log('Enviando dados para API:', requestData)

      const response = await createCatalogoTokenService(requestData)

      if (response.token) {
        const newTokenData = {
          clienteCnpj,
          empresaId,
          token: response.token.token || '',
          dataCriacao: response.token.dataCriacao || new Date().toISOString(),
          dataExpiracao: response.token.dataExpiracao || dataExpiracao.toISOString(),
          ativo: response.token.ativo || true,
        }

        saveCatalogoToken(clienteCnpj, response.token)
        setTokenData(newTokenData)
        toast.success(`Link do catálogo gerado com sucesso! Válido por ${horasExpiracao}h`)
      } else {
        toast.error(response.mensagem || 'Erro ao gerar link do catálogo')
      }
    } catch (error: any) {
      console.error('Erro ao gerar token:', error)
      const errorMessage = error?.response?.data?.mensagem ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Erro ao gerar link do catálogo'
      toast.error(errorMessage)
    } finally {
      setActionLoading(null)
    }
  }

  const handleInvalidateToken = async () => {
    if (!tokenData) return

    setActionLoading('invalidate')
    try {
      const response = await invalidateCatalogoTokenService({
        token: tokenData.token
      })

      if (response.mensagem.includes('sucesso')) {
        removeCatalogoToken(clienteCnpj, empresaId)
        setTokenData(null)
        toast.success('Token do catálogo invalidado com sucesso!')
      } else {
        toast.error(response.mensagem || 'Erro ao invalidar token do catálogo')
      }
    } catch (error) {
      console.error('Erro ao invalidar token:', error)
      toast.error('Erro ao invalidar token do catálogo')
    } finally {
      setActionLoading(null)
    }
  }

  const getCatalogoLink = () => {
    return tokenData ? generateCatalogoLinkFromToken(tokenData.token) : ''
  }

  const handleCopyLink = async () => {
    const link = getCatalogoLink()
    if (!link) return

    try {
      await navigator.clipboard.writeText(link)
      toast.success('Link copiado para a área de transferência!')
    } catch (error) {
      console.error('Erro ao copiar link:', error)
      toast.error('Erro ao copiar link')
    }
  }

  const handleShareWhatsApp = () => {
    const link = getCatalogoLink()
    if (!link) return

    const message = `Olá! Confira nosso catálogo de produtos: ${link}`
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  useEffect(() => {
    if (isOpen && clienteCnpj && hasRequiredData) {
      loadCatalogoToken()
    }
  }, [isOpen, clienteCnpj, empresaId, hasRequiredData])

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md sm:w-full !left-[50%] !translate-x-[-50%]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Link do Catálogo
          </DialogTitle>
          <DialogDescription>
            Gerencie o link do catálogo para <strong>{clienteName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!hasRequiredData ? (
            <div className="py-6 space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-700 font-medium">⚠️ Dados insuficientes</p>
                <p className="text-sm text-yellow-600 mt-1">
                  {!tabelaPrecoId || tabelaPrecoId.trim() === '' ?
                    'Cliente sem tabela de preço configurada.' :
                    'Cliente sem tabela de preço vigência configurada.'
                  }
                </p>
              </div>
              <p className="text-gray-600 text-sm text-center">
                Para gerar o link do catálogo, o cliente precisa ter uma tabela de preço e vigência configuradas.
              </p>

              {/* Seletor de tempo de expiração */}
              <div className="space-y-2">
                <Label htmlFor="expiration-insufficient" className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4" />
                  Tempo de validade
                </Label>
                <Select value={expirationHours} onValueChange={setExpirationHours}>
                  <SelectTrigger id="expiration-insufficient" className="w-full">
                    <SelectValue placeholder="Selecione o tempo de expiração" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hora</SelectItem>
                    <SelectItem value="3">3 horas</SelectItem>
                    <SelectItem value="6">6 horas</SelectItem>
                    <SelectItem value="12">12 horas</SelectItem>
                    <SelectItem value="24">24 horas (1 dia)</SelectItem>
                    <SelectItem value="48">48 horas (2 dias)</SelectItem>
                    <SelectItem value="72">72 horas (3 dias)</SelectItem>
                    <SelectItem value="168">168 horas (7 dias)</SelectItem>
                    <SelectItem value="720">720 horas (30 dias)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleGenerateToken}
                disabled={actionLoading === 'generate'}
                variant="outline"
                className="w-full flex items-center gap-2"
              >
                {actionLoading === 'generate' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Link className="h-4 w-4" />
                )}
                Tentar Gerar Link
              </Button>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Carregando...</span>
            </div>
          ) : tokenData ? (
            <div className="space-y-4">
              {/* Status do token */}
              {!isServerTokenValid(tokenData) ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">⚠️ Link expirado</p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Válido até {formatDate(tokenData.dataExpiracao)}</p>
              )}

              {/* Link com ações principais */}
              <div className="space-y-3">
                <div className="p-3 bg-gray-100 rounded border text-sm font-mono break-all">
                  {getCatalogoLink()}
                </div>

                {/* Ações principais */}
                {isServerTokenValid(tokenData) ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={handleCopyLink}
                      className="flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      Copiar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleShareWhatsApp}
                      className="flex items-center gap-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Seletor de tempo de expiração para renovação */}
                    <div className="space-y-2">
                      <Label htmlFor="expiration-renew" className="flex items-center gap-2 text-sm font-medium">
                        <Clock className="h-4 w-4" />
                        Tempo de validade do novo link
                      </Label>
                      <Select value={expirationHours} onValueChange={setExpirationHours}>
                        <SelectTrigger id="expiration-renew" className="w-full">
                          <SelectValue placeholder="Selecione o tempo de expiração" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 hora</SelectItem>
                          <SelectItem value="3">3 horas</SelectItem>
                          <SelectItem value="6">6 horas</SelectItem>
                          <SelectItem value="12">12 horas</SelectItem>
                          <SelectItem value="24">24 horas (1 dia)</SelectItem>
                          <SelectItem value="48">48 horas (2 dias)</SelectItem>
                          <SelectItem value="72">72 horas (3 dias)</SelectItem>
                          <SelectItem value="168">168 horas (7 dias)</SelectItem>
                          <SelectItem value="720">720 horas (30 dias)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={handleGenerateToken}
                      disabled={actionLoading === 'generate'}
                      className="w-full flex items-center gap-2"
                    >
                      {actionLoading === 'generate' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Gerar Novo Link
                    </Button>
                  </div>
                )}

                {/* Ação secundária */}
                {isServerTokenValid(tokenData) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleInvalidateToken}
                    disabled={actionLoading === 'invalidate'}
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    {actionLoading === 'invalidate' ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Invalidar Link
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="py-6 space-y-4">
              <p className="text-gray-600 text-center mb-4">
                Nenhum link de catálogo encontrado para este cliente.
              </p>

              {/* Seletor de tempo de expiração */}
              <div className="space-y-2">
                <Label htmlFor="expiration" className="flex items-center gap-2 text-sm font-medium">
                  <Clock className="h-4 w-4" />
                  Tempo de validade
                </Label>
                <Select value={expirationHours} onValueChange={setExpirationHours}>
                  <SelectTrigger id="expiration" className="w-full">
                    <SelectValue placeholder="Selecione o tempo de expiração" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hora</SelectItem>
                    <SelectItem value="3">3 horas</SelectItem>
                    <SelectItem value="6">6 horas</SelectItem>
                    <SelectItem value="12">12 horas</SelectItem>
                    <SelectItem value="24">24 horas (1 dia)</SelectItem>
                    <SelectItem value="48">48 horas (2 dias)</SelectItem>
                    <SelectItem value="72">72 horas (3 dias)</SelectItem>
                    <SelectItem value="168">168 horas (7 dias)</SelectItem>
                    <SelectItem value="720">720 horas (30 dias)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleGenerateToken}
                disabled={actionLoading === 'generate'}
                className="w-full flex items-center gap-2"
              >
                {actionLoading === 'generate' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Link className="h-4 w-4" />
                )}
                Gerar Link
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}